import { __t, applyTranslations } from 'utils/language';

applyTranslations(require('./snippets.i18n.json'));

import { DeliverableAttachmentType } from 'types';
import {
  AttachmentLike,
  StorySnippet
} from '../types/general';
import {
  StorySnippetValidationError
} from '../types/validation';


interface LikeReplacedAttachment {
  id: number,
  meta: {
    type: DeliverableAttachmentType
  }
}

interface LikeSnippetWithReplacedAttachment<A extends LikeReplacedAttachment> {
  story: null | A,
  insights: Array<A>
}

export const getReplacedAttachment = <A extends LikeReplacedAttachment>(
  snippets: Array<LikeSnippetWithReplacedAttachment<A>>,
  newAttachment: A,
  oldAttachmentId: number
): null | A => (
    snippets
      .reduce((acc, att) => ([
        ...acc,
        ...(att.story ? [att.story] : ([] as Array<A>)),
        ...att.insights
      ]), [] as Array<A>)
      .find(att => {
        // To function correctly we have to keep old id for story attachment
        if (newAttachment.meta.type === 'highres') {
          return att.id === oldAttachmentId;
        }

        return att.id === newAttachment.id;
      })
    ?? null
  );

export const getAttachmentById = <A extends { id: number }>(
  snippets: Array<{ insights: Array<A>, story: null | A }>,
  id: number
): null | A => (
    snippets
      .reduce((acc, att) => ([
        ...acc,
        ...(att.story ? [att.story] : ([] as Array<A>)),
        ...att.insights
      ]), [] as Array<A>)
      .find(att => att.id === id)
  ?? null
  );

interface SimplestAttachment {
  id: number,
  relatedAssets?: null | Array<number>
}

interface SimplestSnippet<A extends SimplestAttachment> {
  story: null | A,
  insights: Array<A>
}

export const getUpdatedParentAttachment = <A extends SimplestAttachment>(
  snippets: Array<SimplestSnippet<A>>,
  newAttachment: A
): null | A => (
    snippets
      .reduce((acc, att) => ([
        ...acc,
        ...(att.story ? [att.story] : []),
        ...att.insights
      ]), [] as Array<A>)
      .find(
        att => (att.relatedAssets ?? []).find(id => id === newAttachment.id)
      )
    ?? null
  );

export const getSnippetsFromAttachments = <T extends AttachmentLike>(
  attachments: Array<T>
): Array<StorySnippet<T>> => (
    attachments.reduce((acc, val, _, array) => {
      if (val.meta.type === 'highres') {
        const snippet: StorySnippet<T> = {
          id: acc.length,
          story: val,
          insights: array.filter(v => (val.relatedAssets ?? []).includes(v.id)),
          saved: true,
          deleteToReplacePrevAttachmentId: val.id
        };

        acc.push(snippet);
      }

      return acc;
    }, [] as Array<StorySnippet<T>>)
  );

export const getStorySnippetValidationErrors = (
  snippet: { story: null | object }
): Array<StorySnippetValidationError> => {
  const errors: Array<StorySnippetValidationError> = [];

  if (snippet.story === null) {
    errors.push({
      type: 'no-story',
      error: __t('story_snippets_utils.errors.no_story'),
      snackError: __t('story_snippets_utils.errors.no_story_snack')
    });
  }

  return errors;
};

interface AttachmentWithIdAndRelatedAssets {
  id: number,
  relatedAssets?: null | Array<number>
}

interface StorySnippetLike<T> {
  story: null | T,
  insights: Array<T>,
  deleteToReplacePrevAttachmentId?: number
}

export const getUpdatedAttachmentsFromSnippets = <T extends AttachmentWithIdAndRelatedAssets>(
  snippets: Array<StorySnippetLike<T>>,
  attachmentIds: Array<number>
): {
  attachments: Array<T>,
  parent: null | T
} => {
  const allAttachments = snippets.reduce((acc, att) => ([
    ...acc,
    ...(att.story ? [att.story] : []),
    ...att.insights
  ]), [] as Array<T>);
  let attachmentsFromStore = allAttachments
    .filter(att => attachmentIds.includes(att.id));
  attachmentsFromStore = attachmentsFromStore.length === 0
    ? (() => {
      const prevAttachmentIds = snippets
        .filter(snip => attachmentIds.includes(snip.deleteToReplacePrevAttachmentId ?? -1))
        .map(snip => snip.story?.id ?? -1);

      return allAttachments.filter(att => prevAttachmentIds.includes(att.id));
    })()
    : attachmentsFromStore;
  const parent = allAttachments
    .find(att => (att.relatedAssets ?? [])
      .filter(id => attachmentsFromStore.map(val => val.id).includes(id)).length > 0
    ) ?? null;
  return {
    parent,
    attachments: attachmentsFromStore
  };
};
